import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Decorative } from "@faire/design-tokens";
import { MobileSlider } from "@faire/retailer-visitor-shared/components/MobileSlider/index";
import { TrustPilotWidget } from "@faire/retailer-visitor-shared/components/TestimonialCarousel/TrustPilotWidget";
import { useNationality } from "@faire/retailer-visitor-shared/hooks/retailer/useNationality";
import { getTrustpilotRating } from "@faire/retailer-visitor-shared/serialized-data/getTrustpilotRating";
import { BASE_TESTIMONIAL_EU, BASE_TESTIMONIAL_NAM, TESTIMONIAL_LANGUAGE_GROUP, TRUST_PILOT_MINIMUM_RATING, getIsShowingCaseStudies } from "@faire/retailer-visitor-shared/views/brands/landing-page/const";
import { optimizeImageUrl } from "@faire/web--source/common/images/optimizeImages";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { Column, Row, HorizontalLine } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { Link } from "@faire/web--source/ui/LinkAnchor";
import { trackBrandLeadsBrandsPageMobileTestimonialView } from "@faire/web-api--source/events/brandLeads/view/brandsPageMobileTestimonial";
import { trackBrandLeadsTrustPilotBadgeView } from "@faire/web-api--source/events/brandLeads/view/trustPilotBadge";
import brandUrl from "@faire/web-api--source/routes/www/brand/brandToken";
import React, { useCallback, useEffect } from "react";
import styled from "styled-components";
export var BrandsLandingPageTestimonialsSwipe = function() {
    var strictLocalize = useStrictLocalization().strictLocalize;
    var _useNationality = useNationality(), retailerCountry = _useNationality.retailerCountry, isEurope = _useNationality.isEurope;
    var country = retailerCountry || "";
    var countryAvailable = Object.keys(TESTIMONIAL_LANGUAGE_GROUP(strictLocalize)).includes(country);
    var testimonials = useCallback(function(strictLocalize) {
        return function() {
            if (countryAvailable) {
                // @ts-expect-error FIXME(implicitAny): https://faire.link/no-implicit-any
                return TESTIMONIAL_LANGUAGE_GROUP(strictLocalize)[country];
            } else if (isEurope) {
                return BASE_TESTIMONIAL_EU(strictLocalize);
            }
            return BASE_TESTIMONIAL_NAM(strictLocalize);
        }();
    }, [
        country,
        countryAvailable,
        isEurope
    ]);
    useEffect(function() {
        var testimonial = testimonials(strictLocalize)[0];
        if (testimonial) {
            trackBrandLeadsBrandsPageMobileTestimonialView(testimonial.brandToken);
        }
    }, [
        testimonials,
        strictLocalize
    ]);
    var handleSliderChangeHandler = function(_, newIndex) {
        var testimonial = testimonials(strictLocalize)[newIndex];
        if (testimonial) {
            trackBrandLeadsBrandsPageMobileTestimonialView(testimonial.brandToken);
        }
    };
    var viewRef = useDoActionWhenVisible(trackBrandLeadsTrustPilotBadgeView);
    var FAIRE_TRUSTPILOT_RATING = getTrustpilotRating();
    return /*#__PURE__*/ _jsxs(SwipeContainer, {
        children: [
            !getIsShowingCaseStudies() ? /*#__PURE__*/ _jsx(Column, {
                style: {
                    padding: getSpacing("3x")
                },
                children: /*#__PURE__*/ _jsx(Typography, {
                    variant: "pageHeaderSerifRegular",
                    children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                        id: "ls2+xL",
                        defaultMessage: [
                            {
                                type: 0,
                                value: "How brands have grown their businesses on Faire"
                            }
                        ]
                    })
                })
            }) : /*#__PURE__*/ _jsx(Spacer, {
                height: "3x"
            }),
            /*#__PURE__*/ _jsx(MobileSlider, {
                numberOfSlides: testimonials.length,
                centerMode: true,
                arrows: false,
                centerPadding: "24px",
                infinite: true,
                beforeChange: handleSliderChangeHandler,
                slidesToShow: 1,
                slidesToScroll: 1,
                children: testimonials(strictLocalize).map(function(testimonial, index) {
                    return /*#__PURE__*/ _jsx("div", {
                        children: /*#__PURE__*/ _jsx(Testimonial, {
                            testimonial: testimonial
                        })
                    }, "".concat(testimonial.brandToken, "_").concat(index));
                })
            }),
            getIsShowingCaseStudies() && FAIRE_TRUSTPILOT_RATING >= TRUST_PILOT_MINIMUM_RATING ? /*#__PURE__*/ _jsxs(Column, {
                ref: viewRef,
                align: "center",
                style: {
                    padding: getSpacing(0, "3x")
                },
                children: [
                    /*#__PURE__*/ _jsx(HorizontalLine, {
                        color: Decorative.tint.green
                    }),
                    /*#__PURE__*/ _jsx(Spacer, {
                        height: "3x"
                    }),
                    /*#__PURE__*/ _jsx(TrustPilotWidget, {
                        rating: FAIRE_TRUSTPILOT_RATING
                    }),
                    /*#__PURE__*/ _jsx(Spacer, {
                        height: "4x"
                    })
                ]
            }) : null
        ]
    });
};
var Testimonial = function(param) {
    var testimonial = param.testimonial;
    var isSSR = useIsSSR();
    return /*#__PURE__*/ _jsx(SwipeCardContainer, {
        children: /*#__PURE__*/ _jsxs(SwipeCard, {
            children: [
                /*#__PURE__*/ _jsx(TypographyScroll, {
                    variant: "paragraphSansMedium",
                    children: testimonial.content
                }),
                /*#__PURE__*/ _jsx(Spacer, {
                    size: "1x"
                }),
                /*#__PURE__*/ _jsxs(Row, {
                    children: [
                        /*#__PURE__*/ _jsx("img", {
                            style: {
                                width: "39px",
                                height: "39px",
                                borderRadius: "100%"
                            },
                            src: optimizeImageUrl({
                                url: testimonial.brandImageUrl
                            }, isSSR)
                        }),
                        /*#__PURE__*/ _jsx(Spacer, {
                            size: "1x"
                        }),
                        /*#__PURE__*/ _jsxs(Column, {
                            children: [
                                /*#__PURE__*/ _jsx(Typography, {
                                    variant: "paragraphSansMedium",
                                    children: /*#__PURE__*/ _jsxs(Link, {
                                        to: brandUrl(testimonial.brandToken),
                                        children: [
                                            testimonial.ownerName,
                                            ", ",
                                            testimonial.brandName
                                        ]
                                    })
                                }),
                                /*#__PURE__*/ _jsxs(Typography, {
                                    variant: "labelSansRegular",
                                    children: [
                                        testimonial.ownerCity ? testimonial.ownerCity + ", " : "",
                                        testimonial.brandRegion
                                    ]
                                })
                            ]
                        })
                    ]
                })
            ]
        })
    });
};
var SwipeContainer = styled(Column).withConfig({
    componentId: "sc-e7548894-0"
})([
    "background-color:",
    ";width:100%;height:fit-content;"
], Decorative.tint.neutral);
var SwipeCardContainer = styled(Row).withConfig({
    componentId: "sc-e7548894-1"
})([
    "background-color:transparent;padding:4px;"
]);
var TypographyScroll = styled(Typography).withConfig({
    componentId: "sc-e7548894-2"
})([
    "overflow-y:auto;"
]);
var SwipeCard = styled(Column).attrs({
    justify: "center"
}).withConfig({
    componentId: "sc-e7548894-3"
})([
    "padding:",
    ";background-color:white;height:270px;border-radius:4px;"
], getSpacing("3x"));
